/**
 * Chart helper utilities for DataCloud
 * UI components and helper functions for chart rendering
 */
import React from 'react';

/**
 * Parse axis label from header array
 * Extracts non-UTC header with unit format [unit]
 * @param headers Array of header strings
 * @returns Formatted axis label or null
 */
export const parseAxisLabel = (headers: string[] | undefined): string | null => {
  if (!headers || headers.length === 0) return null;

  // Find first non-UTC header
  const nonUtcHeader = headers.find(h => h.toLowerCase() !== 'utc') || headers[0];
  if (!nonUtcHeader) return null;

  // Try to extract name and unit from format "Name [unit]"
  const unitMatch = nonUtcHeader.match(/^(.*?)\s*\[(.*?)\]$/);
  return unitMatch
    ? `${unitMatch[1].trim()} [${unitMatch[2].trim()}]`
    : nonUtcHeader;
};

/**
 * Checkmark icon component for file upload indicators
 */
export const CheckIcon: React.FC = () =>
  React.createElement('svg', {
    xmlns: 'http://www.w3.org/2000/svg',
    className: 'h-5 w-5 mr-1',
    viewBox: '0 0 20 20',
    fill: 'currentColor',
    children: React.createElement('path', {
      fillRule: 'evenodd',
      d: 'M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z',
      clipRule: 'evenodd'
    })
  });

/**
 * Format file size for display
 * @param bytes File size in bytes
 * @returns Formatted string (e.g., "1.5 KB" or "2.3 MB")
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * Format file size from megabytes to human-readable format
 * @param mb File size in megabytes
 * @returns Formatted string (e.g., "500 B", "1.5 KB", "2.3 MB", "1.2 GB")
 */
export const formatFileSizeFromMB = (mb: number): string => {
  // Handle very small or zero values (likely due to rounding in database)
  if (mb <= 0) return '< 1 KB';
  if (mb < 0.001) return '< 1 KB'; // Less than ~1KB
  
  const bytes = mb * 1024 * 1024;
  if (bytes < 1024) return `${Math.round(bytes)} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

/**
 * Generate unique upload ID
 * @returns Unique string ID for upload tracking
 */
export const generateUploadId = (): string => {
  return `cloud_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
};

/**
 * Create timestamp string in German locale
 * @param date Date object (defaults to now)
 * @returns Formatted timestamp string
 */
export const formatTimestamp = (date: Date = new Date()): string => {
  return date.toLocaleString('de-DE', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
};

/**
 * Check if arrays have valid regression data
 * @param data Array of data points to check
 * @param key Key to check (e.g., 'regressionValue', 'upperBound')
 * @returns True if at least one valid value exists
 */
export const hasValidData = <T extends Record<string, unknown>>(
  data: T[],
  key: keyof T
): boolean => {
  return data.some(d => {
    const value = d[key];
    return value !== undefined && value !== null && !isNaN(value as number);
  });
};

/**
 * Extract array values from data points, with fallback
 * @param data Array of data points
 * @param key Primary key to extract
 * @param fallbackKey Fallback key if primary is invalid
 * @returns Array of values
 */
export const extractChartValues = <T extends Record<string, number | undefined>>(
  data: T[],
  key: keyof T,
  fallbackKey?: keyof T
): number[] => {
  return data.map(d => {
    const value = d[key];
    if (value !== undefined && !isNaN(value)) {
      return value;
    }
    if (fallbackKey) {
      const fallback = d[fallbackKey];
      return fallback !== undefined && !isNaN(fallback) ? fallback : 0;
    }
    return 0;
  });
};
